/** Dosage math + display helpers for the Pool & Spa advisor (ported from _POOL APP). */
import type { ChemicalProduct, ChemicalUnit } from '../types';

export interface DosageResult {
  amount: number;
  unit: ChemicalUnit;
}

/** US fluid ounce → millilitres. */
export const ML_PER_FL_OZ = 29.5735;
/** Avoirdupois ounce (weight) → grams. */
export const G_PER_OZ = 28.3495;

/**
 * Calculates the dosage for a chemical product.
 *
 * Handles two formula schemas from the database:
 *  - Schema A (balancers): { baseAmount, baseUnit, perVolume, raisesBy }
 *    e.g. 1.5 lbs per 10,000 gal raises TA by 10 ppm
 *    For pH products, changeNeeded should be the pH delta (e.g. 0.4), NOT multiplied by 10.
 *  - Schema B (chlorine/sanitizers): { amountPer10kGalToOnePpm, unit }
 *    e.g. 10.4 fl oz per 10,000 gal raises FC by 1 ppm
 */
export function calculateDosage(
  product: ChemicalProduct,
  changeNeeded: number,
  poolVolume: number
): DosageResult | null {
  const f = product.formula;
  if (!f || changeNeeded <= 0 || poolVolume <= 0) return null;

  // Schema B: amountPer10kGalToOnePpm (chlorine shocks and sanitizers)
  if (f.amountPer10kGalToOnePpm != null && f.unit != null) {
    return {
      amount: changeNeeded * (poolVolume / 10000) * f.amountPer10kGalToOnePpm,
      unit: f.unit,
    };
  }

  // Schema A: baseAmount / raisesBy (balancers: TA, pH, CH, CYA)
  if (
    f.baseAmount != null &&
    f.baseUnit != null &&
    f.perVolume != null &&
    f.raisesBy != null
  ) {
    const raisesBy =
      typeof f.raisesBy === 'string' ? parseFloat(f.raisesBy) : f.raisesBy;
    if (!raisesBy || isNaN(raisesBy)) return null;
    return {
      amount:
        (Math.abs(changeNeeded) / Math.abs(raisesBy)) *
        f.baseAmount *
        (poolVolume / f.perVolume),
      unit: f.baseUnit,
    };
  }

  return null;
}

/**
 * Metric equivalent of a dose:
 *  - liquid volumes (fl_oz / quarts / gallons / ml) → millilitres (always)
 *  - dry weights (oz / lbs) → grams (always)
 *  - tablets / bags → no metric equivalent (returns null)
 */
export function toMetric(
  amount: number,
  unit: ChemicalUnit
): { amount: number; unit: 'ml' | 'g' } | null {
  let ml: number | null = null;
  let g: number | null = null;
  switch (unit) {
    case 'ml':
      ml = amount;
      break;
    case 'fl_oz':
      ml = amount * ML_PER_FL_OZ;
      break;
    case 'quarts':
      ml = amount * 32 * ML_PER_FL_OZ;
      break;
    case 'gallons':
      ml = amount * 128 * ML_PER_FL_OZ;
      break;
    case 'oz':
      g = amount * G_PER_OZ;
      break;
    case 'lbs':
      g = amount * 16 * G_PER_OZ;
      break;
    default:
      return null;
  }
  if (ml !== null) return { amount: ml, unit: 'ml' };
  if (g !== null) return { amount: g, unit: 'g' };
  return null;
}

/** Format a metric equivalent like "(429 ml)" / "(3785 ml)" / "(454 g)". Empty for tablets/bags. */
export function formatMetricSuffix(amount: number, unit: ChemicalUnit): string {
  const metric = toMetric(amount, unit);
  if (!metric) return '';
  if (unit === 'ml') return ''; // already metric — no duplicate suffix
  return ` (${String(Math.round(metric.amount))} ${metric.unit})`;
}

/**
 * Annotates US-unit quantities inside free-text usage instructions with
 * their metric equivalents, e.g. "Add 2 oz per 10,000 gallons" becomes
 * "Add 2 oz (57 g) per 10,000 gallons". Handles "fl oz" / "fl. oz."
 * (→ ml, always), plain "oz" (→ g), and "lb"/"lbs" (→ g, always). Quantities
 * already followed by a metric annotation are left untouched.
 */
export function annotateUsageWithMetric(text: string): string {
  const pattern = /(\d+(?:\.\d+)?)\s*(fl\.?\s?oz|lbs?|oz)\b\.?/gi;
  return text.replace(
    pattern,
    (match, qtyStr: string, unitRaw: string, offset: number) => {
      const rest = text.slice(offset + match.length);
      // Skip when a metric annotation already follows (e.g. "(59 ml)").
      if (/^\s*[(/]\s*(?:~|≈)?\s*[\d.]+\s*(ml|l|g|kg)\b/i.test(rest)) {
        return match;
      }
      const qty = Number.parseFloat(qtyStr);
      if (!Number.isFinite(qty) || qty <= 0) return match;
      const unit = unitRaw.toLowerCase();
      let value: number;
      let metricUnit: 'ml' | 'g';
      if (unit.startsWith('fl')) {
        value = qty * ML_PER_FL_OZ;
        metricUnit = 'ml';
      } else if (unit.startsWith('lb')) {
        value = qty * 16 * G_PER_OZ;
        metricUnit = 'g';
      } else {
        value = qty * G_PER_OZ;
        metricUnit = 'g';
      }
      return `${match} (${String(Math.round(value))} ${metricUnit})`;
    }
  );
}

export function formatDosageAmount(amount: number, unit: ChemicalUnit): string {
  const metric = formatMetricSuffix(amount, unit);
  if (unit === 'tablets') return `${Math.ceil(amount)} tablets`;
  if (unit === 'bags') return `${Math.ceil(amount)} bags`;
  if (unit === 'lbs') return `${amount.toFixed(2)} lbs${metric}`;
  if (unit === 'oz') return `${amount.toFixed(2)} oz${metric}`;
  if (unit === 'fl_oz') return `${amount.toFixed(1)} fl oz${metric}`;
  if (unit === 'gallons') return `${amount.toFixed(1)} gallons${metric}`;
  if (unit === 'quarts') return `${amount.toFixed(1)} quarts${metric}`;
  if (unit === 'ml') return `${Math.round(amount)} ml`;
  return `${amount.toFixed(2)} ${unit}`;
}
